import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToastContext } from '../contexts/ToastContext';
import useGeolocation from './useGeolocation';
import { normalizeDetectedCity, formatLocationDisplay } from '../utils/userLocationDisplay';
import { useUpdateBusinessProfile, vendorProfileKeys } from '../services/vendorProfileService';
import { useQueryClient } from '@tanstack/react-query';

const COUNTRY_ALIASES = {
  gh: 'ghana',
  ghana: 'ghana',
  us: 'united states',
  usa: 'united states',
  'united states': 'united states',
  'united states of america': 'united states',
  ca: 'canada',
  canada: 'canada',
  uk: 'united kingdom',
  gb: 'united kingdom',
  'united kingdom': 'united kingdom',
  ng: 'nigeria',
  nigeria: 'nigeria',
  ke: 'kenya',
  kenya: 'kenya',
  za: 'south africa',
  'south africa': 'south africa',
  au: 'australia',
  australia: 'australia',
  de: 'germany',
  germany: 'germany',
  fr: 'france',
  france: 'france',
};

export const normalizeCountryName = (country) => {
  if (!country) return '';
  const key = String(country).trim().toLowerCase();
  return COUNTRY_ALIASES[key] || key;
};

export const areCountriesMatching = (countryA, countryB) => {
  if (!countryA || !countryB) return false;
  const normA = normalizeCountryName(countryA);
  const normB = normalizeCountryName(countryB);
  return normA === normB;
};

export const areCitiesMatching = (cityA, cityB) => {
  if (!cityA || !cityB) return false;
  return String(cityA).trim().toLowerCase() === String(cityB).trim().toLowerCase();
};

export const useVendorLocationAlert = () => {
  const { user, updateUser, isAuthenticated } = useAuth();
  const toast = useToastContext();
  const queryClient = useQueryClient();
  const [dismissedKey, setDismissedKey] = useState(null);

  const {
    location: geoLocation,
    address: geoAddress,
    city: geoCity,
    state: geoState,
    country: geoCountry,
    countryCode: geoCountryCode,
    loading: geoLoading,
    getCurrentLocation,
  } = useGeolocation();

  // Extract saved vendor profile location
  const savedLocation = useMemo(() => {
    const vp = user?.vendor_profile || {};
    const country = vp.country || user?.country || '';
    const city = vp.city || user?.city || '';
    const state = vp.state || user?.state || '';
    const address = vp.address || user?.address || '';
    const latitude = vp.latitude != null ? vp.latitude : user?.latitude != null ? user.latitude : null;
    const longitude = vp.longitude != null ? vp.longitude : user?.longitude != null ? user.longitude : null;
    const display = formatLocationDisplay({ city, state, country }) || country || 'Not configured';

    return {
      country,
      city,
      state,
      address,
      latitude,
      longitude,
      display,
      hasLocation: Boolean(country || city),
    };
  }, [user]);

  // Extract detected live location
  const detectedLocation = useMemo(() => {
    const country = geoCountry || '';
    const countryCode = geoCountryCode || '';
    const state = geoState || '';
    const rawCity = geoCity || '';
    const city = normalizeDetectedCity({ city: rawCity, state, country }) || rawCity;
    const address = geoAddress || '';
    const latitude = geoLocation?.latitude != null ? geoLocation.latitude : null;
    const longitude = geoLocation?.longitude != null ? geoLocation.longitude : null;
    const display = formatLocationDisplay({ city, state, country }) || country || '';

    return {
      country,
      countryCode,
      city,
      state,
      address,
      latitude,
      longitude,
      display,
      hasDetectedLocation: Boolean(country || city || (latitude != null && longitude != null)),
    };
  }, [geoCountry, geoCountryCode, geoState, geoCity, geoAddress, geoLocation]);

  // Compute mismatch logic
  const isMismatch = useMemo(() => {
    if (!isAuthenticated || user?.user_type !== 'vendor') return false;
    if (!detectedLocation.hasDetectedLocation || !savedLocation.hasLocation) return false;

    // Compare Country
    if (detectedLocation.country && savedLocation.country) {
      if (!areCountriesMatching(detectedLocation.country, savedLocation.country)) {
        return true;
      }
    }

    // If country matches but cities are explicitly different
    if (
      detectedLocation.city &&
      savedLocation.city &&
      !areCitiesMatching(detectedLocation.city, savedLocation.city)
    ) {
      return true;
    }

    return false;
  }, [isAuthenticated, user?.user_type, detectedLocation, savedLocation]);

  const currentMismatchKey = useMemo(() => {
    if (!isMismatch) return null;
    return `vendor_loc_dismiss_${normalizeCountryName(detectedLocation.country)}_${(detectedLocation.city || '').toLowerCase()}`;
  }, [isMismatch, detectedLocation.country, detectedLocation.city]);

  // Check if current mismatch has been dismissed for this session
  const isDismissed = useMemo(() => {
    if (!currentMismatchKey) return false;
    try {
      return sessionStorage.getItem(currentMismatchKey) === 'true';
    } catch {
      return dismissedKey === currentMismatchKey;
    }
  }, [currentMismatchKey, dismissedKey]);

  // Trigger initial geolocation detection for vendor if needed
  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'vendor') {
      getCurrentLocation({ preferIp: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.user_type]);

  const dismissAlert = useCallback(() => {
    if (currentMismatchKey) {
      try {
        sessionStorage.setItem(currentMismatchKey, 'true');
      } catch {
        // fallback
      }
      setDismissedKey(currentMismatchKey);
    }
  }, [currentMismatchKey]);

  const updateBusinessProfileMutation = useUpdateBusinessProfile();

  const updateToDetectedLocation = useCallback(async () => {
    if (!detectedLocation.hasDetectedLocation) {
      toast?.error?.('Location detection unavailable', 'Could not detect your current location coordinates.');
      return;
    }

    const payload = {
      country: detectedLocation.country || savedLocation.country || 'US',
      city: detectedLocation.city || savedLocation.city || '',
      state: detectedLocation.state || savedLocation.state || '',
      ...(detectedLocation.address ? { address: detectedLocation.address } : {}),
      ...(detectedLocation.latitude != null ? { latitude: detectedLocation.latitude } : {}),
      ...(detectedLocation.longitude != null ? { longitude: detectedLocation.longitude } : {}),
    };

    try {
      const result = await updateBusinessProfileMutation.mutateAsync(payload);
      
      const newVp = {
        ...(user?.vendor_profile || {}),
        ...(result?.data || {}),
        ...payload,
      };

      const updatedUser = {
        ...user,
        country: payload.country,
        city: payload.city,
        state: payload.state,
        latitude: payload.latitude || user?.latitude,
        longitude: payload.longitude || user?.longitude,
        vendor_profile: newVp,
      };

      updateUser(updatedUser);
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.profile() });
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.business() });

      if (currentMismatchKey) {
        try {
          sessionStorage.removeItem(currentMismatchKey);
        } catch {
          // fallback
        }
      }

      toast?.success?.(
        'Business Location Updated',
        `Your business operating location has been updated to ${formatLocationDisplay(payload)}.`
      );
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to update business location';
      toast?.error?.('Update failed', msg);
    }
  }, [detectedLocation, savedLocation, updateBusinessProfileMutation, user, updateUser, queryClient, currentMismatchKey, toast]);

  const refreshDetection = useCallback(() => {
    getCurrentLocation({ preferIp: true });
  }, [getCurrentLocation]);

  return {
    isMismatch,
    isDismissed,
    savedLocation,
    detectedLocation,
    isDetecting: geoLoading,
    isUpdating: updateBusinessProfileMutation.isPending,
    dismissAlert,
    updateToDetectedLocation,
    refreshDetection,
  };
};

export default useVendorLocationAlert;
